/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-multi-spaces */
// eslint-disable-next-line no-restricted-imports
import invert from 'lodash.invert';
import isUndefined from 'lodash.isundefined';
import pick from 'lodash.pick';
// eslint-disable-next-line no-restricted-imports
import {NextRouter} from 'next/router';
import qs from 'qs';
import RouteManager from '@ion/applications/src/routes/RouteManager';
import {localPersistence} from '@ion/core/src/capabilities/storage';
import {translate} from '@ion/core/src/locale/locale';
import {info} from '@ion/core/src/logger/logger';
import {
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  DEFAULT_QUERY_PARAMS,
  getQueryString,
  removeQueryString
} from '@ion/core/src/utils/QueryStringUtils';
import {
  titleCase
} from '@ion/core/src/utils/StringUtil';
import {ApplicationKeys} from '@/types/application';
import {
  ADD_ONS_CRASHBOXX,
  ADD_ONS_ELD,
  ADD_ONS_ION_VISION,
  ADD_ONS_ION_VISION_EVENTS,
  ADMIN_ALERTS_PAGE,
  ADMIN_ASSETS_PAGE,
  ADMIN_DEVICES_PAGE,
  ADMIN_DRIVER_SCORECARD,
  ADMIN_GROUPS_PAGE,
  ADMIN_IO_PROFILES_PAGE,
  ADMIN_LANDMARK_GEOFENCE_PAGE,
  ADMIN_MAINTENANCE_PAGE,
  ADMIN_OPERATORS_PAGE,
  ADMIN_ROLES_PAGE,
  ADMIN_ROUTES_PAGE,
  ADMIN_SCHEDULES_PAGE,
  ADMIN_USERS_PAGE,
  ANALYTICS_PAGE,
  DASHBOARD_PAGE,
  EVENTS_GEOFENCES_PAGE,
  EVENTS_NOTIFICATIONS_PAGE,
  MAINTENANCE_DASHBOARD,
  MAINTENANCE_PAGE,
  REPORT_MANAGER_PAGE,
  TRACKING_PAGE,
  TRACKING_PAGE2,
  TRACKING_SUPPLY_CHAIN,
  TRIP,
  TRIP_LIST,
  VEHICLE_INSPECTIONS_RESULTS,
  VEHICLE_INSPECTIONS_SETUP,
  VISION_ASSETS,
  VISION_COACHING,
  VISION_CONFIGURATIONS,
  VISION_DRIVERS,
  VISION_HEALTH,
  VISION_HOME,
  VISION_LIVE,
  VISION_REPORTS
} from '../security/Features';

// defines all predefined route definitions
export enum Route {
  LOGIN                   = 'login',
  DASHBOARD               = 'dashboard',
  FLEET_DASHBOARD         = 'dashboard/fleet',
  CRASHBOX_DASHBOARD      = 'dashboard/crashboxx',
  SISENCE_DASHBOARD       = 'dashboard/sisense',
  TRACKING                = 'tracking',
  TRACKING2               = 'tracking2',
  TRIP                    = 'trip',
  TRIP_LIST               = 'trip/list',
  ISSUES                  = 'issues',
  ANALYTICS               = 'analytics',
  REPORTS                 = 'reports',
  VISION                  = 'vision',
  VISION_LIBRARY          = 'vision/library',
  VISION_EVENTS           = 'vision/events',
  VISION_HOME             = 'vision/home',
  VISION_COACHING         = 'vision/coaching',
  VISION_CONFIGURATIONS   = 'vision/configurations',
  VISION_LIVE             = 'vision/live',
  VISION_REPORTS          = 'vision/reports',
  VISION_HEALTH           = 'vision/health',
  VISION_DRIVERS          = 'vision/drivers',
  VISION_ASSETS           = 'vision/assets',
  ADD_ONS_ELD             = 'addonseld',
  ADD_ONS_PAGE_CRASHBOXX  = 'addonscrashboxx',
  ANALYTICS_DASHBOARDS    = 'analytics/dashboards',
  ANALYTICS_SCHEDULER     = 'analytics/scheduler',
  REPORTS_REPORT          = 'reports/[reportId]',
  CRASHBOXX               = 'crashboxx',
  MAINTENANCE             = 'maintenancepage',
  MAINTENANCE_DASHBOARD   = 'maintenance',
  MAINTENANCE_SCHEDULE    = 'maintenance/[assetId]',
  INSPECTIONS             = 'inspections',
  INSPECTIONS_SETUP       = 'inspections/setup',
  INSPECTIONS_RESULTS     = 'inspections/results',
  EVENTS                  = 'events',
  EVENTS_GEOFENCES        = 'events/geofences',
  EVENTS_NOTIFICATIONS    = 'events/notifications',
  ADMIN                   = 'admin',
  ADMIN_ASSETS            = 'admin/assets',
  ADMIN_DEVICES           = 'admin/devices',
  ADMIN_DRIVERS           = 'admin/drivers',
  ADMIN_GROUPS            = 'admin/groups',
  ADMIN_ROUTES            = 'admin/routes',
  ADMIN_ROLES             = 'admin/roles',
  ADMIN_USERS             = 'admin/users',
  ADMIN_GEOFENCES         = 'admin/geofences',
  ADMIN_NOTIFICATIONS     = 'admin/notifications',
  ADMIN_MAINTENANCE       = 'admin/maintenance',
  ADMIN_SCHEDULES         = 'admin/schedules',
  ADMIN_IOPROFILES        = 'admin/ioprofiles',
  ADMIN_DRIVER_SCORING    = 'admin/driverscoring',
  ELD                     = 'eld',
  ADD_ONS_VISION          = 'vision'
};

const FeaturesMap = {
  [DASHBOARD_PAGE]               : Route.FLEET_DASHBOARD,
  [ADD_ONS_CRASHBOXX]            : Route.CRASHBOX_DASHBOARD,
  [TRACKING_PAGE]                : Route.TRACKING,
  [TRACKING_PAGE2]               : Route.TRACKING2,
  [TRIP]                         : Route.TRIP,
  [TRIP_LIST]                    : Route.TRIP_LIST,
  [EVENTS_GEOFENCES_PAGE]        : Route.EVENTS_GEOFENCES,
  [EVENTS_NOTIFICATIONS_PAGE]    : Route.EVENTS_NOTIFICATIONS,
  [TRACKING_SUPPLY_CHAIN]        : Route.REPORTS,
  [MAINTENANCE_PAGE]             : Route.MAINTENANCE,
  [MAINTENANCE_DASHBOARD]        : Route.MAINTENANCE_DASHBOARD,
  [VEHICLE_INSPECTIONS_RESULTS]  : Route.INSPECTIONS_RESULTS,
  [VEHICLE_INSPECTIONS_SETUP]    : Route.INSPECTIONS_SETUP,
  [ADMIN_ASSETS_PAGE]            : Route.ADMIN_ASSETS,
  [ADMIN_DEVICES_PAGE]           : Route.ADMIN_DEVICES,
  [ADMIN_OPERATORS_PAGE]         : Route.ADMIN_DRIVERS,
  [ADMIN_GROUPS_PAGE]            : Route.ADMIN_GROUPS,
  [ADMIN_ROUTES_PAGE]            : Route.ADMIN_ROUTES,
  [ADMIN_ROLES_PAGE]             : Route.ADMIN_ROLES,
  [ADMIN_USERS_PAGE]             : Route.ADMIN_USERS,
  [ADMIN_ALERTS_PAGE]            : Route.ADMIN_NOTIFICATIONS,
  [ADMIN_MAINTENANCE_PAGE]       : Route.ADMIN_MAINTENANCE,
  [ADMIN_SCHEDULES_PAGE]         : Route.ADMIN_SCHEDULES,
  [ADMIN_IO_PROFILES_PAGE]       : Route.ADMIN_IOPROFILES,
  [ADMIN_LANDMARK_GEOFENCE_PAGE] : Route.ADMIN_GEOFENCES,
  [ADMIN_DRIVER_SCORECARD]       : Route.ADMIN_DRIVER_SCORING,
  [ADD_ONS_ION_VISION]           : Route.VISION_LIBRARY,
  [ADD_ONS_ION_VISION_EVENTS]    : Route.VISION_EVENTS,
  [VISION_HOME]                  : Route.VISION_HOME,
  [VISION_COACHING]              : Route.VISION_COACHING,
  [VISION_CONFIGURATIONS]        : Route.VISION_CONFIGURATIONS,
  [VISION_LIVE]                  : Route.VISION_LIVE,
  [VISION_REPORTS]               : Route.VISION_REPORTS,
  [VISION_HEALTH]                : Route.VISION_HEALTH,
  [VISION_DRIVERS]               : Route.VISION_DRIVERS,
  [VISION_ASSETS]                : Route.VISION_ASSETS,
  [ANALYTICS_PAGE]               : Route.ANALYTICS_DASHBOARDS,
  [REPORT_MANAGER_PAGE]          : Route.ANALYTICS_SCHEDULER,
  [ADD_ONS_ELD]                  : Route.ELD
};

// the route map to the pages that are using
// duplicates feature ids...
const RouteFeatureMap = {
  [Route.CRASHBOXX]         : ADD_ONS_CRASHBOXX,
  [Route.SISENCE_DASHBOARD] : DASHBOARD_PAGE
};

// defines the dynamic reports page id interpolation token ...
export const REPORTS_ID_TOKEN = '[reportId]';

// defines the dynamic maintenance page asset id interpolation token ...
export const ASSET_ID_TOKEN   = '[assetId]';

/**
 * Maps legacy (deprecated) routes to their current,
 * supported route equivalents.
 *
 * This map is used to maintain backward compatibility
 * for older URLs, bookmarks, or deep links that still
 * reference legacy paths.
 *
 * Only routes that have changed should be listed here.
 * All other routes will pass through unchanged.
 *
 * @example
 * ```ts
 * legacyToCurrentRouteMap['/vision'];
 * // → Route.VISION_LIBRARY
 * ```
 */
export const legacyToCurrentRouteMap: Partial<Record<string, Route>> = {
  '/tracking'           : Route.TRACKING2,
  '/vision'             : Route.VISION_LIBRARY,
  '/vision/vision'      : Route.VISION_LIBRARY,
  'vision/visionevents' : Route.VISION_EVENTS
};

/**
 * Provides the initial point of entry for configuring iOn Suite Route management.
 *
 * Invoked when the root application component (_app.js) is first mounted, after which
 * all query changes are referenced for subsequent inclusion within each route when the
 * current page changes.
 */
export const initializeRouter = () => {
  info('Initializing Application Router ...');
  setQuery();
  RouteManager.addEventListener('routeChangeStart', function() {
    setQuery();
  });
};

/**
 * Normalizes a route by converting any legacy route
 * to its current equivalent.
 *
 * If the provided route is not a legacy route,
 * it is returned unchanged.
 *
 * This function acts as a single source of truth
 * for route backward compatibility.
 *
 * @param route - A route that may be legacy or current.
 * @returns The normalized (current) route.
 *
 * @example
 * ```ts
 * normalizeRoute('/vision');
 * // → Route.VISION_LIBRARY
 *
 * normalizeRoute(Route.VISION_LIBRARY);
 * // → Route.VISION_LIBRARY
 * ```
 */
export const normalizeRoute = (route: Route): Route => {
  const key = route?.startsWith('/') ? route : `/${route}`;
  return legacyToCurrentRouteMap[key] ?? legacyToCurrentRouteMap[route] ?? route;
};

/**
 * Provides the single point of entry for changing a page within iOn Suite. This function
 * must be used explicitly and Next Router may not be used directly.
 *
 * The changePage function allows for ensuring all pages meet the requirements of iOn Suite,
 * including, but not limited to, verifying Route definitions, inclusion of existing query
 * parameters, etc..
 *
 * ```typescript
 *
 *   import changePage {Route} from '@/routes/RouteDefinitions';
 *
 *   // navigate to the admin assets page, including existing query parameters by
 *   // default ...
 *   changePage(Route.ADMIN_ASSETS);
 *
 *   // navigate to a specific reports page (e.g. /reports/123), including existing
 *   // query parameters, by default ...
 *   changePage(Route.REPORTS, `/${data.id}`);
 *
 *   // navigate to the admin assets page, excluding existing query parameters ...
 *   changePage(Route.ADMIN_ASSETS, null, false);
 *
 *   // navigate to a specific reports page (e.g. /reports/123), excluding existing
 *   // query parameters ....
 *   changePage(Route.REPORTS, `/${data.id}`, false);
 *
 * ```
 *
 * @param {Route} route The Route to which the application is to navigate.
 * @param {string} path An optional path to include within the route.
 * @param {boolean} includeParams When true (default), the previous pages query parameters
 */
export const changePage = (route: Route, path = '', includeParams = true): void => {
  const url: string = getRoute(normalizeRoute(route), path);
  let query: string = '';
  if (!isObjectEmpty(_query)) {
    if (includeParams) {
      query = qs.stringify(_query);
    } else {
      query = getDefaultQueryString(false);
    }
  }
  RouteManager.navigate({
    pathname : removeQueryString(url),
    query    : query || getQueryString(url)
  });
};

/**
 * Utility function to return default query param string to update the URL path
 *
 * ```typescript
 *
 *   import {getDefaultQueryString} from '@/routes/RouteDefinitions';
 *
 *   getDefaultQueryString(); //?environment=us&oidcEnabled=true
 *   getDefaultQueryString(false); //environment=us&oidcEnabled=true
 *
 * ```
 *
 * @param {boolean} withQuestionMark When true (default), '?' is prefixed in query string
 */
export const getDefaultQueryString = (withQuestionMark = true): string => {
  const defaultQuery = pick(_query || {}, DEFAULT_QUERY_PARAMS);
  let defaultQueryString = '';
  if (!isObjectEmpty(defaultQuery)) {
    defaultQueryString = `${qs.stringify(defaultQuery, {addQueryPrefix: withQuestionMark})}`;
  }
  return defaultQueryString;
};

/**
 * The getDefaultHash function provides a default hash which should be the
 * append to the URL to the given route. This ensures that the route hash
 * mapping to the given route.
 *
 * ```typescript
 *
 *  import {getDefaultHash} from '../routes/RouteDefinitions';
 *
 *  const hash = getDefaultHash('/reports');
 *
 * ```
 */
export const getDefaultHash = (route: Route | string) => {
  if (route.includes(Route.REPORTS)) {
    return 'report';
  }
};

/**
 * The getRoute function provides the single point of access which should be used
 * for retrieving a given route. The helper ensures that the route being referenced
 * has the correct base path prefix and maps to an existing, predefined route.
 *
 * @param {Route} route The Route to which the application is to navigate.
 * @param {string} path An optional path to include within the route.
 */
export const getRoute = (route: Route, path?: string): string => {
  return `/${route?.replace(/^\/+/, '')}${path || ''}`.split('?')?.[0];
};

/**
 * Provides a utility which returns the currently persisted application route
 * from local storage.
 *
 * @returns the currently persisted application `Route`.
 */
export const getPersistedRoute = (): Route => {
  const persisted = localPersistence(ApplicationKeys.iOnSuitePage, 'route') as Route;
  if (!persisted) {
    return persisted;
  }
  const normalized = normalizeRoute(persisted);
  if (persisted.startsWith('/')) {
    return getRoute(normalized) as Route;
  } else {
    return normalized;
  }
};

/**
 * Provides a utility which sets the currently persisted application route to
 * local storage.
 *
 * @param the route which is to be persisted to local storage.
 */
export const setPersistedRoute = (page: string): void => {
  localPersistence(ApplicationKeys.iOnSuitePage, 'route', page?.split('?')?.[0]);
};

/**
 * @private
 */
let _query;

/**
 * Provides a reference to the current query parameters passed to the current page,
 * or forwarded to the current page from subsequent pages.
 */
export const getQuery = () => {
  return _query;
};

/**
 * Allows for setting the query parameters to be passed to each iOn Suite page when
 * the page is loaded. This function should generally not be used directly as it is
 * primarily used internally for updating the query parameters passed to iOn Suite
 * pages.
 */
export const setQuery = (querystring?: string): void => {
  _query = qs.parse(querystring || location.search, {ignoreQueryPrefix: true});
};

/**
 * Provides a utility for determining if the application is being run in debug mode
 * via the `?debug? Querystring parameter.
 *
 * This function is primarily intended for determining if the application is to be
 * run in debug mode prior to initializing the router in order to enable / disable
 * logging in higher environments.
 */
export const isDebug = (): boolean => {
  const query = getQuery() || qs.parse(location.search, {ignoreQueryPrefix: true});
  return !isUndefined(query?.debug);
};

/**
 * Provides a utility which determines if the provided route is the current route.
 */
export const isRoute = (route: Route): boolean => {
  return window.location.pathname.indexOf(route) > -1;
};

/**
 * Provides a utility which determines if the given `Route` or path is valid.
 *
 * @param   route Either a pre-defined `Route` or URI path.
 * @returns true if the provides route or path is valid, otherwise, false.
 */
export const isValidPath = (route: Partial<NextRouter>): boolean => {
  return !!(route?.pathname && route?.pathname !== '/');
};

/**
 * The getRouteDefinition function provides a utility which returns the Route for
 * the given path
 *
 * ```typescript
 *    import {getRouteDefinition} from '@/routes/RouteDefinitions';
 *
 *    getRouteDefinition('/tracking'); // Route.TRACKING
 *    getRouteDefinition('/admin/assets'); // Route.ADMIN_ASSETS
 *
 * ```
 */
export const getRouteDefinition = (path: string): Route => {
  const route = path?.replace(/^\/|\/$/g, '').split('?')?.[0];
  const name  = Object.keys(Route).find((name: string) => {
    return Route[name] === route;
  });
  return Route[name];
};

/**
 * The getRouteByFeature function provides a utility which returns the iOn Route for
 * the given iOn Feature.
 *
 * ```typescript
 *    import {getRouteDefinition} from '@/routes/RouteDefinitions';
 *
 *    getRouteByFeature(TRACKING); // Route.TRACKING
 *    getRouteByFeature(ADMIN_ASSETS); // Route.ADMIN_ASSETS
 *
 * ```
 */
export const getRouteByFeature = (id: string, fallback = true): Route => {
  let route = FeaturesMap[id];
  if (!route && fallback) {
    route = Route.TRACKING2;
  }
  return route;
};

/**
 * The getFeatureByRoute method provides a utility which returns the feature id for
 * the given iOn Route.
 *
 * ```typescript
 *
 *    import {getFeatureByRoute} from '@/routes/RouteDefinitions';
 *
 *    getFeatureByRoute(Route.TRACKING); // TRACKING
 *    getFeatureByRoute(Route.ADMIN_ASSETS); // ADMIN_ASSETS
 *
 * ```
 */
export const getFeatureByRoute = (id: Route | string): string => {
  return invert(FeaturesMap)[id] || RouteFeatureMap[id];
};

/**
 * The getRouteLabel function provides a utility which returns the route Label for
 * the given Route
 *
 * ```typescript
 *    import {getRouteLabel} from '@/routes/RouteDefinitions';
 *
 *    getRouteDefinition(Route.ADMIN_ASSETS); // adminAssets
 *    getRouteDefinition(Route.ADMIN_DEVICES); // adminDevices
 *
 * ```
 */
export const getRouteLabel = (route: string, useTitleCase = false, labelId?: string): string => {
  if (!labelId && route.match('/')) {
    const [rootRoute, subRoute] = route.split('/');
    const capital = subRoute.charAt(0);
    const page    = subRoute.replace(capital, capital.toUpperCase());
    const entry   = translate(`nav:side.${rootRoute}${page}`);
    return useTitleCase ? titleCase(entry) : entry;
  }
  const label = labelId ? translate(`nav:side.${labelId}`) : route;
  return useTitleCase ? titleCase(label) : label;
};

/**
 * The getPageTitle function provides a simple utility which returns the page title
 * for the given route.
 *
 * ```typescript
 *   import {getPageTitle} from '@/routes/RouteDefinitions';
 *
 *   getPageTitle(Route.ADMIN_ASSETS); // Assets
 *   getPageTitle(Route.TRACKING); // Tracking
 *
 * ```
 */
export const getPageTitle = (route: Route | string) => {
  if (route) {
    let [, prefix, postfix] = route.substr(0, route.length).split('/');
    if (prefix.includes(Route.TRACKING)) {
      return '';
    }
    if (prefix.includes(Route.REPORTS)) {
      return translate('nav:side.gatewayHistory');
    }
    // if postfix contains a string integer just display the prefix
    if (parseInt(postfix)) {
      return translate(`nav:side.${prefix}`);
    }
    if (postfix) {
      postfix = `${postfix.charAt(0).toUpperCase()}${postfix.slice(1)}`;
      return translate(`nav:side.${prefix}${postfix}`);
    }
    return translate(`nav:side.${prefix}`);
  }
};

export default changePage;
